import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { toggleResponseState } from "../../../Redux/reducers/responseSlice";
import RandomizationPanel from "../../Randomization/Participant/RandomizationPanel";

/**
 * The `randomize` question type. Randomization spec 21.3, 21.8, D26.
 *
 * IT RENDERS THE PARTICIPANT PANEL — the same component the participant record
 * renders — with `triggerSource = "form_question"`. That is the whole content
 * of this file on purpose. The previous version was a second implementation of
 * the same flow against the old endpoint: its own eligibility check, its own
 * confirmation dialog, its own error handling. Two implementations of "may this
 * participant be randomized, and what happens when they are" is one rule and
 * one bug, and the copy inside a form question is the one nobody remembers to
 * update.
 *
 * WHAT THE QUESTION RESPONSE STORES is `{ randomizationNumber, allocationId }`,
 * written by the server when the allocation is made. That response is
 * IMMUTABLE and is NOT altered by a later reversal (spec 21.8): the reversal is
 * visible on the participant panel and on the timeline, not by rewriting a form
 * answer that was true when it was given.
 *
 * `viewRandomization` and `surveySlug` still gate whether the field appears at
 * all, exactly as before — the field is hidden in the participant-facing survey
 * view and shown only in the admin response view.
 *
 * @category Randomization
 */
const Randomize = () => {
  const { id: studyId, surveySlug, participantId } = useParams();
  const dispatch = useAppDispatch();

  const viewRandomization = useAppSelector(
    (state) => state.response.viewRandomization,
  );

  if (surveySlug || !viewRandomization || !studyId || !participantId) {
    return null;
  }

  return (
    <Stack
      sx={{
        width: {
          xs: "100%",
          md: "80%",
        },
        pl: "60px",
      }}
      gap={1}
    >
      <RandomizationPanel
        studyId={studyId}
        participantId={participantId}
        triggerSource="form_question"
        // Embedded: no outer card, because it already sits inside the
        // question's own layout.
        embedded
        // Refreshes the surrounding response view so the stored answer appears
        // without a reload. The panel's own cache is invalidated by RTK Query.
        onAllocated={() => dispatch(toggleResponseState())}
      />
    </Stack>
  );
};

export default Randomize;
